// frontend/src/pages/admin/EditCategoryPage.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Globe, Upload, X } from 'lucide-react';
import { SEOEditor } from '../../components/SEO/SEOEditor';
import { PageSEO } from '../../components/SEO/PageSEO';
import config from '../../config';
// در بالای فایل، بعد از imports اضافه کن
const slugify = (text) => {
  if (!text) return '';

  let slug = text
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/[^\w\u0600-\u06FF\-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  if (!slug) {
    slug = 'category-' + Date.now();
  }
  return slug;
};
export const EditCategoryPage = () => {
  const { id } = useParams();  // این id می‌تواند عدد یا slug باشد
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSEOPanel, setShowSEOPanel] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [manualSlug, setManualSlug] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    parent: '',
  });
  const [icon, setIcon] = useState(null);
  const [seoData, setSeoData] = useState({
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    focus_keyword: '',
  });
  const [categoriesList, setCategoriesList] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      if (id && id !== 'new') {
        try {
          // ابتدا لیست همه دسته‌بندی‌ها را بگیریم تا ببینیم id چیست
          const listUrl = config.getApiUrl('/products/categories/');
          console.log('Fetching categories list from:', listUrl);
          const listResponse = await fetch(listUrl);

          if (!listResponse.ok) {
            throw new Error(`HTTP ${listResponse.status}: خطا در دریافت لیست دسته‌بندی‌ها`);
          }

          const listData = await listResponse.json();
          const categories = listData.results || listData || [];
          setCategoriesList(categories);

          // پیدا کردن دسته‌بندی مورد نظر (با id یا slug)
          let category = null;

          // اول با id عددی جستجو کن
          category = categories.find(c => c.id === parseInt(id));

          // اگر پیدا نشد، با slug جستجو کن
          if (!category) {
            category = categories.find(c => c.slug === id);
          }

          // اگر باز هم پیدا نشد، مستقیماً با آدرس کامل تلاش کن
          if (!category) {
            // تلاش با آدرس مستقیم (با id)
            const directUrl = config.getApiUrl(`/products/categories/${id}/`);
            console.log('Trying direct URL:', directUrl);
            const directResponse = await fetch(directUrl);

            if (directResponse.ok) {
              category = await directResponse.json();
            } else {
              // تلاش با slug
              const slugUrl = config.getApiUrl(`/products/categories/${id}/`);
              const slugResponse = await fetch(slugUrl);
              if (slugResponse.ok) {
                category = await slugResponse.json();
              }
            }
          }

          if (!category) {
            throw new Error('دسته‌بندی یافت نشد');
          }

          console.log('Category found:', category);

          setFormData({
            name: category.name,
            slug: category.slug,
            description: category.description || '',
            parent: category.parent || '',
          });
          setManualSlug(true);
          setIcon(category.icon);
          setSeoData({
            meta_title: category.meta_title || category.name,
            meta_description: category.meta_description || category.description?.substring(0, 160) || '',
            meta_keywords: category.meta_keywords || '',
            focus_keyword: category.focus_keyword || '',
          });
        } catch (error) {
          console.error('Error fetching category:', error);
          alert(`خطا در بارگذاری اطلاعات: ${error.message}`);
          navigate('/admin/categories');
        } finally {
          setLoading(false);
        }
      } else {
        // دسته‌بندی جدید - فرم خالی
        setLoading(false);
        // تنظیم مقادیر پیش‌فرض
        setFormData({
          name: '',
          slug: '',
          description: '',
          parent: '',
        });
        setManualSlug(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'name') {
      const newName = value;
      if (!manualSlug) {
        const generatedSlug = slugify(newName);
        setFormData(prev => ({ ...prev, name: newName, slug: generatedSlug }));
      } else {
        setFormData(prev => ({ ...prev, name: newName }));
      }
    } else if (name === 'slug') {
      // ✅ فقط حذف کاراکترهای کاملاً غیرمجاز (نه حذف فاصله و خط تیره)
      // کاربر می‌تواند هر چیزی تایپ کند، فقط در نهایت پاکسازی می‌شود
      let cleanSlug = value
        .trim()
        // فقط حذف کاراکترهای خیلی خاص (مثل @#$%^&*)
        .replace(/[^\w\u0600-\u06FF\s\-_]/g, '')  // ← اینجا \s و \- و _ را نگه داشته
        .replace(/\s+/g, '-')     // فاصله → خط تیره (در انتها)
        .replace(/_/g, '-')        // زیرخط → خط تیره
        .replace(/-+/g, '-')       // خط تیره تکراری → یک خط تیره
        .replace(/^-|-$/g, '');    // حذف خط تیره از ابتدا و انتها

      setFormData(prev => ({ ...prev, slug: cleanSlug }));
      setManualSlug(true);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const accessToken = localStorage.getItem('access_token');

      const updateData = {
        name: formData.name,
        slug: formData.slug,
        description: formData.description,
        parent: formData.parent || null,
        meta_title: seoData.meta_title,
        meta_description: seoData.meta_description,
        meta_keywords: seoData.meta_keywords,
        focus_keyword: seoData.focus_keyword,
      };

      let url, method;

      // بررسی whether it's a new category or existing
      const isNew = id === 'new' || !id;

      if (isNew) {
        // ایجاد دسته‌بندی جدید - استفاده از POST
        url = config.getApiUrl('/products/categories/');
        method = 'POST';
      } else {
        // ✅ پیدا کردن slug اصلی از categoriesList
        const originalCategory = categoriesList.find(c => c.id === parseInt(id) || c.slug === id);
        const originalSlug = originalCategory?.slug || formData.slug;
        url = config.getApiUrl(`/products/categories/${encodeURIComponent(originalSlug)}/`);
        method = 'PUT';
      }

      console.log('Sending request to:', url, 'with method:', method);
      console.log('Update data:', updateData);

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        alert(isNew ? 'دسته‌بندی با موفقیت ایجاد شد' : 'دسته‌بندی با موفقیت ویرایش شد');
        navigate('/admin/categories');
      } else {
        const error = await response.json();
        alert('خطا: ' + JSON.stringify(error));
      }
    } catch (error) {
      console.error('Error saving category:', error);
      alert('خطا در ارتباط با سرور');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <PageSEO title="ویرایش دسته‌بندی | پنل ادمین" noindex={true} />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900">ویرایش دسته‌بندی</h1>
              <button
                type="button"
                onClick={() => setShowSEOPanel(!showSEOPanel)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
              >
                <Globe className="w-4 h-4" />
                {showSEOPanel ? 'بستن تنظیمات سئو' : 'تنظیمات سئو (پیشرفته)'}
              </button>
            </div>

            {showSEOPanel && (
              <SEOEditor
                initialData={seoData}
                onSave={(newSeoData) => {
                  setSeoData(newSeoData);
                  setShowSEOPanel(false);
                  alert('تنظیمات سئو ذخیره شد (برای ذخیره نهایی، فرم را ارسال کنید)');
                }}
                onCancel={() => setShowSEOPanel(false)}
                content={formData.description}
                title="تنظیمات سئوی دسته‌بندی"
              />
            )}

            {/* بخش آیکون */}
            <div className="mb-8 p-4 bg-gray-50 rounded-lg">
              <h2 className="text-lg font-semibold mb-4">آیکون دسته‌بندی</h2>
              <div className="flex items-center gap-4">
                {icon && (
                  <img src={icon} alt="Category Icon" className="w-16 h-16 object-contain" />
                )}
                <label className="flex items-center justify-center w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition bg-white">
                  <div className="text-center">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <span className="text-xs text-gray-500">{uploadingImage ? 'در حال آپلود...' : 'آپلود آیکون'}</span>
                  </div>
                  <input type="file" accept="image/*" className="hidden" disabled={uploadingImage} />
                </label>
              </div>
            </div>

            {/* فرم ویرایش */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">نام دسته‌بندی *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  آدرس یکتا (slug)
                  <span className="text-xs text-gray-500 mr-2">(فارسی، انگلیسی، اعداد و خط تیره مجاز است)</span>
                </label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 font-mono"
                  placeholder="مثال: دسته-بندی-جدید"
                />
                <small className="text-xs text-gray-500">
                  آدرس نهایی: {config.SITE_URL}/category/{formData.slug || '...'}
                </small>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">توضیحات</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows="4"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">دسته‌بندی والد</label>
                <select
                  value={formData.parent}
                  onChange={(e) => setFormData(prev => ({ ...prev, parent: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">بدون والد</option>
                  {categoriesList.filter(c => c.id !== parseInt(id)).map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => navigate('/admin/categories')} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                  انصراف
                </button>
                <button type="submit" disabled={submitting} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
